import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { graphql } from '@apollo/react-hoc';
import { compose } from 'react-recompose';
import { connect } from 'react-redux';
import get from 'lodash/get';
import { FormattedMessage, defineMessages } from 'react-intl';
import { toGlobalId } from 'graphql-relay';
import { Link } from 'react-router';
import { SimpleLoadingComponent } from '../../utils/apollo';
import UpdateUserForm from '../../components/UpdateUserForm';
import Container from '../../components/Container';
import PageTitle from '../../components/PageTitle';
import Header from '../../components/Header';
import Icon from '../../components/Icon';
import updateHEAccount from '../../mutations/HEAccount/updateHEAccount';

const messages = defineMessages({
  updateUser: {
    id: 'user.update.caption',
    defaultMessage: 'Edit {name}',
  },
});

export class UpdateUserPageComponent extends React.PureComponent {
  static propTypes = {
    params: PropTypes.shape({
      userId: PropTypes.string,
    }),
    location: PropTypes.shape({
      state: PropTypes.shape({
        linkBack: PropTypes.object,
      }),
    }),
    node: PropTypes.object.isRequired,
    updateHEAccountMutation: PropTypes.func.isRequired,
    router: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
  };

  state = {
    updateError: {},
    requesting: false,
  };

  getLinkBack() {
    return get(this.props, 'location.state.linkBack', null);
  }

  getUser() {
    /* istanbul ignore else */
    if (this.props.params && this.props.params.userId && this.props.node && this.props.node.userList) {
      return this.props.node.userList.find((user) => user.id === this.props.params.userId);
    } else return null;
  }

  navigateToCaller() {
    /* istanbul ignore next */
    const link = '/settings/manageUsers';
    const path = this.getLinkBack() || link;
    this.props.router.push(path);
  }

  _handleCancel = () => {
    this.navigateToCaller();
  };

  _handleSave = (updatedUser) => {
    const { updateHEAccountMutation, node: { id }, params: { userId } } = this.props;
    const onFailure = (error) => {
      const newState = {
        requesting: false,
      };
      if (!error) {
        newState.updateError = { generalError: true };
      } else if (error.message.includes('already exists')) {
        newState.updateError = { userExists: true };
      } else {
        newState.updateError = { generalError: true };
      }
      this.setState(newState);
    };

    const onSuccess = () => {
      this.navigateToCaller();
    };

    this.setState({ updateError: {}, requesting: true });
    return updateHEAccountMutation({
      variables: {
        input: {
          heAccount: {
            id,
            heUser: {
              ...updatedUser,
              id: userId,
            },
          },
        },
      },
    }).then(onSuccess).catch(onFailure);
  };

  render() {
    const { updateError, requesting } = this.state;
    const linkBack = this.getLinkBack();
    const user = this.getUser();
    if (!user) {
      return (
        <p>
          <PageTitle title="User" category="Settings" />
          <FormattedMessage id="user.update.notFound" defaultMessage="No User Found" />
        </p>
      );
    }
    return (
      <Container text>
        <PageTitle title="User" category="Settings" />
        {
          linkBack &&
          (<Link to={linkBack}><Icon name="angle left" />Back</Link>)
        }
        <Header as="h1">
          <FormattedMessage {...messages.updateUser} values={{ name: `${user.firstName} ${user.lastName}` }} />
        </Header>
        <UpdateUserForm
          user={user}
          requesting={requesting}
          onCancel={this._handleCancel}
          onSave={this._handleSave}
          updateError={updateError}
        />
      </Container>
    );
  }
}

const query = gql`
  query UpdateUserPageComponent($id: ID!) {
    node(id: $id){
      id
      ... on HEAccount {
        id
        primaryUser {
          id
          firstName
          lastName
          email
          active
          role
          lockStatus
        }
        userList {
          id
          firstName
          lastName
          email
          active
          role
          lockStatus
        }
      }
    }
  }
`;

const graphqlData = graphql(query, {
  options: ({ current }) => ({
    variables: {
      id: toGlobalId('HEAccount', current.viewer.idToken['custom:institution']),
    },
    fetchPolicy: 'cache-and-network',
  }),
});

const updateHEAccountMutation = graphql(updateHEAccount, { name: 'updateHEAccountMutation' });

const mapStateToProps = (state) => ({
  current: state.current,
});

export default compose(
  connect(mapStateToProps),
  SimpleLoadingComponent(graphqlData, updateHEAccountMutation),
)(UpdateUserPageComponent);
